/**
 * Sync - history. Historical operational record for synchronization activity.
 *
 * Answers: what ran, when, how long, outcome, and which entity was affected.
 *
 * Columns: Started · Data type · Status · Duration · Records · Finished.
 * Data type and Status filters live in the column headers. Clear sits above.
 *
 * Wires `GET /api/v1/sync/history` (entity, status, limit, offset).
 * Pagination is heuristic: Next enabled only on a full page.
 */
import { el, mount } from "../utils/dom";
import { renderPageHeader } from "../components/pageHeader";
import {
  renderLoadingState,
  renderErrorState,
  renderEmptyState,
  renderUnavailableState,
} from "../components/states";
import { renderStatusText, runStatusTone } from "../components/statusText";
import { renderAbsoluteTime, renderDuration } from "../components/dateTime";
import { ENTITY_ORDER, entityLabel, formatNumber } from "../utils/format";
import { getSyncHistory } from "../services/sync";
import { ApiError } from "../services/apiClient";
import { restoreWindowScroll, stashSyncHistoryState, takeSyncHistoryState } from "../app/viewState";
import type { SyncHistoryRow } from "../types/api";

const LIMIT_OPTIONS = [25, 50, 100, 200];
/** Backend-documented filter values only - do not invent cancelled here. */
const STATUS_OPTIONS = ["running", "success", "partial", "failed"];

export class SyncHistoryPage {
  private body: HTMLElement = el("div", {}, [renderLoadingState("Loading sync history…")]);
  private paginationEl: HTMLElement = el("div", {});
  private entitySelect!: HTMLSelectElement;
  private statusSelect!: HTMLSelectElement;
  private entity = "";
  private status = "";
  private limit = 50;
  private offset = 0;
  private lastPageCount = 0;
  private navigate: (path: string) => void = () => undefined;
  private pendingScrollY: number | null = null;

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    this.navigate = navigate;

    const restored = takeSyncHistoryState();
    if (restored) {
      this.entity = restored.entity;
      this.status = restored.status;
      this.limit = restored.limit;
      this.offset = restored.offset;
      this.pendingScrollY = restored.scrollY;
    }

    this.entitySelect = headerSelect(
      "Filter by data type",
      [
        el("option", { value: "" }, ["Data type"]),
        ...ENTITY_ORDER.map((e) => el("option", { value: e }, [entityLabel(e)])),
      ],
      (value) => {
        this.entity = value;
        this.offset = 0;
        void this.load();
      }
    );
    this.entitySelect.value = this.entity;
    syncHeaderActive(this.entitySelect, this.entity !== "");

    this.statusSelect = headerSelect(
      "Filter by status",
      [
        el("option", { value: "" }, ["Status"]),
        ...STATUS_OPTIONS.map((s) => el("option", { value: s }, [runStatusTone(s).label])),
      ],
      (value) => {
        this.status = value;
        this.offset = 0;
        void this.load();
      }
    );
    this.statusSelect.value = this.status;
    syncHeaderActive(this.statusSelect, this.status !== "");

    const clearButton = el(
      "button",
      {
        className: "btn-link",
        type: "button",
        onclick: () => this.clearFilters(),
      },
      ["Clear filters"]
    );

    const toolbar = el("div", { className: "toolbar toolbar--range" }, [
      el("div", {}),
      clearButton,
    ]);

    const refreshButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        "aria-label": "Refresh sync history",
        onclick: () => void this.load(),
      },
      ["Refresh"]
    );

    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: "Sync History",
          subtitle: "Past sync runs and outcomes.",
          back: { fallbackPath: "/dashboard", navigate, preferHistory: false, label: "Back to Dashboard" },
          actions: [refreshButton],
        }),
        el("div", { className: "panel panel--standard" }, [toolbar, this.body, this.paginationEl]),
      ])
    );

    void this.load();
  }

  destroy(): void {
    stashSyncHistoryState({
      entity: this.entity,
      status: this.status,
      limit: this.limit,
      offset: this.offset,
      scrollY: window.scrollY,
    });
  }

  private clearFilters(): void {
    this.entity = "";
    this.status = "";
    this.entitySelect.value = "";
    this.statusSelect.value = "";
    syncHeaderActive(this.entitySelect, false);
    syncHeaderActive(this.statusSelect, false);
    this.offset = 0;
    void this.load();
  }

  private async load(): Promise<void> {
    mount(this.body, renderLoadingState("Loading sync history…"));
    mount(this.paginationEl, null);

    try {
      const rows = await getSyncHistory({
        entity: this.entity || undefined,
        status: this.status || undefined,
        limit: this.limit,
        offset: this.offset,
      });

      this.lastPageCount = rows.length;

      if (!rows.length) {
        mount(
          this.body,
          el("div", {}, [
            this.renderTable([]),
            renderEmptyState(this.offset > 0 ? "No more runs." : "No sync runs match these filters."),
          ])
        );
      } else {
        mount(this.body, this.renderTable(rows));
      }

      mount(this.paginationEl, this.renderPagination());

      if (this.pendingScrollY !== null) {
        restoreWindowScroll(this.pendingScrollY);
        this.pendingScrollY = null;
      }
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 503)) {
        mount(
          this.body,
          renderUnavailableState(err.message, "Sync history could not be loaded.")
        );
      } else {
        mount(this.body, renderErrorState(errorMessage(err), { onRetry: () => void this.load() }));
      }
    }
  }

  private renderTable(rows: SyncHistoryRow[]): HTMLElement {
    const table = el("table", { className: "data-table" }, [
      el("thead", {}, [
        el("tr", {}, [
          el("th", { scope: "col" }, ["Started"]),
          filterHeader(this.entitySelect),
          filterHeader(this.statusSelect),
          el("th", { scope: "col" }, ["Duration"]),
          el("th", { scope: "col" }, ["Records"]),
          el("th", { scope: "col" }, ["Finished"]),
        ]),
      ]),
      el(
        "tbody",
        {},
        rows.map((row) => {
          const tone = runStatusTone(row.status);
          const flagClass = rowFlagClass(row.status);
          return el(
            "tr",
            {
              className: ["data-table__row--nav", flagClass].filter(Boolean).join(" "),
              tabIndex: 0,
              role: "button",
              "aria-label": `Run ${row.id}: ${entityLabel(row.entity)} ${tone.label}`,
              onclick: () => this.navigate(`/sync/history/${row.id}`),
              onkeydown: (ev: KeyboardEvent) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  this.navigate(`/sync/history/${row.id}`);
                }
              },
            },
            [
              el("td", {}, [renderAbsoluteTime(row.startedAt)]),
              el("td", {}, [entityLabel(row.entity)]),
              el("td", {}, [renderStatusText(tone.label, tone.tone, { pulse: tone.pulse })]),
              el("td", {}, [renderDuration(row.startedAt, row.completedAt)]),
              el("td", { className: "tnum" }, [formatNumber(row.recordsProcessed)]),
              el("td", {}, [renderAbsoluteTime(row.completedAt)]),
            ]
          );
        })
      ),
    ]);

    return el("div", { className: "data-table-wrap data-table-wrap--fit" }, [table]);
  }

  private renderPagination(): HTMLElement {
    const limitSelect = el(
      "select",
      {
        className: "input input--sm",
        "aria-label": "Rows per page",
        onchange: (ev: Event) => {
          this.limit = Number((ev.target as HTMLSelectElement).value);
          this.offset = 0;
          void this.load();
        },
      },
      LIMIT_OPTIONS.map((n) =>
        el("option", { value: String(n), selected: n === this.limit }, [`${n} / page`])
      )
    );

    const prevButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        disabled: this.offset === 0,
        "aria-label": "Previous page",
        onclick: () => {
          this.offset = Math.max(0, this.offset - this.limit);
          void this.load();
        },
      },
      ["Prev"]
    );

    const nextButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        disabled: this.lastPageCount < this.limit,
        "aria-label": "Next page",
        onclick: () => {
          this.offset = this.offset + this.limit;
          void this.load();
        },
      },
      ["Next"]
    );

    return el("div", { className: "pagination-bar" }, [
      el("span", { className: "next-cron__empty" }, [
        `Showing ${formatNumber(this.lastPageCount)} run${this.lastPageCount === 1 ? "" : "s"} from offset ${formatNumber(this.offset)}`,
      ]),
      el("div", { className: "row row--wrap" }, [limitSelect, prevButton, nextButton]),
    ]);
  }
}

function stopRow(ev: Event): void {
  ev.stopPropagation();
}

function headerSelect(
  ariaLabel: string,
  options: HTMLElement[],
  onChange: (value: string) => void
): HTMLSelectElement {
  const select = el(
    "select",
    {
      className: "input input--header",
      "aria-label": ariaLabel,
      onclick: stopRow,
      onchange: (ev: Event) => {
        const value = (ev.target as HTMLSelectElement).value;
        syncHeaderActive(select, value !== "");
        onChange(value);
      },
    },
    options
  ) as HTMLSelectElement;
  return select;
}

function syncHeaderActive(control: HTMLElement, active: boolean): void {
  control.classList.toggle("input--header-active", active);
}

function filterHeader(control: HTMLElement): HTMLElement {
  return el("th", { scope: "col", className: "data-table__th-filter" }, [control]);
}

function rowFlagClass(status: string): string {
  if (status === "failed") return "flag-danger";
  if (status === "partial") return "flag-accent";
  return "";
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong loading this data.";
}
